/**
 * WorkspaceTablePicker — Wraps TableBrowser with client-side search,
 * pagination, and state management for workspace tables.
 *
 * Unlike the connector table path (server-side pagination, lazy column loading),
 * workspace tables are already fully loaded in memory via useWorkspaceTables,
 * so search and pagination are purely client-side.
 *
 * Usage:
 *   <WorkspaceTablePicker
 *     tables={workspaceTables}
 *     selectionMode="multi"
 *     selectedTables={selected}
 *     onSelectionChange={setSelected}
 *   />
 */
import React, { useState, useMemo, useCallback } from 'react';
import TableBrowser from './TableBrowser';
import { workspaceTablesToEntries } from './workspaceTableAdapter';
import { previewWorkspaceTable } from '../../services/workspaceApi';
import type { WorkspaceTable } from '../../services/workspaceApi';
import type { TableEntry } from './TableBrowser';

export interface WorkspaceTablePickerProps {
  tables: WorkspaceTable[];
  loading?: boolean;
  // Selection
  selectionMode: 'single' | 'multi' | 'none';
  selectedTables: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  // Column selection (optional — off for transform input, on for export filtering)
  columnSelectionEnabled?: boolean;
  selectedColumns?: Map<string, string[]>;
  onColumnSelectionChange?: (table: string, cols: string[]) => void;
  // Preview (optional — requires sessionId for workspace query)
  sessionId?: string;
  // Layout
  pageSize?: number;
  maxHeight?: number;
  compact?: boolean;
}

const WorkspaceTablePicker: React.FC<WorkspaceTablePickerProps> = ({
  tables,
  loading = false,
  selectionMode,
  selectedTables,
  onSelectionChange,
  columnSelectionEnabled = false,
  selectedColumns,
  onColumnSelectionChange,
  sessionId,
  pageSize = 20,
  maxHeight,
  compact = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  // Convert workspace tables to TableEntry format (all in memory, columns included)
  const allEntries = useMemo(() => workspaceTablesToEntries(tables), [tables]);

  // Client-side search filtering
  const filteredEntries = useMemo(() => {
    if (!searchValue.trim()) return allEntries;
    const q = searchValue.toLowerCase();
    return allEntries.filter((e) => e.name.toLowerCase().includes(q));
  }, [allEntries, searchValue]);

  // Client-side pagination
  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [filteredEntries, page, pageSize]);

  // Reset page when search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  // Handle preview — fetch sample rows from workspace API
  const handlePreviewTable = useCallback(
    async (tableName: string) => {
      if (!sessionId) return;
      try {
        const preview = await previewWorkspaceTable(sessionId, tableName, 10);
        // Mutate the entry in allEntries to add preview rows
        // We use a functional approach — find and update the entry
        const entry = paginatedEntries.find((e) => e.name === tableName);
        if (entry) {
          entry.previewRows = preview.rows;
          entry.previewLoading = false;
        }
      } catch {
        // Preview failed silently — the TableBrowser will just not show preview
      }
    },
    [sessionId, paginatedEntries],
  );

  // Columns are already loaded — no-op for onExpandTable
  // (TableBrowser calls this when a row is expanded, but workspace tables already have columns)

  return (
    <TableBrowser
      tables={paginatedEntries}
      loading={loading}
      totalCount={filteredEntries.length}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      selectionMode={selectionMode}
      selectedTables={selectedTables}
      onSelectionChange={onSelectionChange}
      columnSelectionEnabled={columnSelectionEnabled}
      selectedColumns={selectedColumns}
      onColumnSelectionChange={onColumnSelectionChange}
      onPreviewTable={sessionId ? handlePreviewTable : undefined}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
    />
  );
};

export default WorkspaceTablePicker;
